import { useMutation, useQuery } from "@tanstack/react-query";
import { SubscriptionBlocked } from "@/features/auth/components/SubscriptionBlocked";
import { SubscriptionExpiryBanner } from "@/features/auth/components/SubscriptionExpiryBanner";
import { TenantBar } from "@/features/auth/components/TenantBar";
import { TenantPicker } from "@/features/auth/components/TenantPicker";
import { env } from "@/lib/env";
import { reloadIntoIdentity } from "@/lib/auth/identity-switch";
import { isSubscriptionBlocked } from "@/features/auth/components/subscription-notice";
import { needsTenantChoice, showsTenantContext } from "@/features/auth/components/tenant-choice";
import { mustChangePassword } from "@/features/auth/components/password-change";
import { PasswordChangeRequired } from "@/features/auth/components/PasswordChangeRequired";
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { brandedName } from "@/app/layouts/branded-name";
import { useAuth } from "@/app/providers/AuthProvider";
import { decidesForOthers } from "@/lib/permissions/permissions";
import { useTheme } from "@altek/ui/components/theme/ThemeProvider";
import { AppNavbar, ProfileMenu } from "@altek/ui/components/layout/AppNavbar";
import { AppSidebar, type SidebarLinkProps } from "@altek/ui/components/layout/AppSidebar";
import { MobileNavScrim } from "@altek/ui/components/layout/Header";
import { activeHrefFor, buildSidebarGroups } from "@altek/ui/components/layout/sidebar-model";
import {
  ALWAYS_VISIBLE_PANELS,
  COMPANY_GROUP_TITLE,
  NAV_PANELS,
  navCatalogFor,
  type NavPanel,
} from "@/app/navigation/nav-catalog";
import { SkBlock, SkLine } from "@altek/ui/components/feedback/Skeleton";
import { Button } from "@altek/ui/components/ui/Button";
import {
  CommandPalette,
  type CommandGroup,
  type CommandItem,
} from "@altek/ui/components/ui/CommandPalette";
import {
  PERSONAL_SCREENS,
  SECTION_SCREENS,
  codeLookupActions,
  matchesTerm,
  personalScreenTitles,
  recordSearchSources,
  screenAllowed,
  searchPlaceholderFor,
  useRecordSearch,
} from "./global-search";
import {
  OFF_MENU_TITLES,
  ScreenTitleContext,
  composeDocumentTitle,
  screenTitleFor,
  titledPathsOf,
  withKeptPrefix,
  type TitledPath,
} from "./document-title";
import { CloseOnNavigation } from "./CloseOnNavigation";
import { CompanySlot } from "./CompanySlot";
import { useModalSheet } from "./useModalSheet";
import { usePaletteFocusReturn } from "./usePaletteFocusReturn";
import { usePaletteShortcut } from "./palette-shortcut";
import {
  featuredQuickActions,
  quickActionItemId,
  quickActionsFor,
  shellCommandOf,
  type QuickAction,
} from "@/app/navigation/quick-actions";
import { BlockingScreen } from "./BlockingScreen";
import { useSignOut } from "./useSignOut";
import { adminApi } from "@/features/admin/api/admin.api";
import {
  IMPERSONATION_WINDOW_KEY,
  ImpersonateDialog,
} from "@/features/admin/components/ImpersonateDialog";
import { myProfileQuery } from "@/features/directory/api/directory.queries";
import { ChatDockProvider } from "@/features/chat/components/ChatDockProvider";
import { useChatDock } from "@/features/chat/components/chat-dock-context";
import { ChatLauncherHost } from "@/features/chat/components/ChatLauncherHost";
import { chatReadable } from "@/features/chat/lib/chat-launcher-visibility";
import { MainScroller } from "./MainScroller";
import { MEDIA, useMediaQuery } from "@/hooks/useMediaQuery";
import { useScope } from "@/hooks/useScope";
import { closedGroupsFrom, domSafeGroupId, openGroupsFrom, withDomSafeIds } from "./rail-groups";
import { settingsHrefFor } from "./settings-row";
import { NotificationsPanel } from "@/features/notifications/components/NotificationsPanel";
import { ApprovalsPanel } from "@/features/approvals/components/ApprovalsPanel";
import { approvalInboxQuery } from "@/features/approvals/api/approvals.queries";
import { eventListQuery } from "@/features/events/api/events.queries";
import { unreadCountQuery } from "@/features/notifications/api/notifications.queries";
import { ChatRealtimeBridge } from "@/features/chat/realtime/ChatRealtimeBridge";
import { PushSubscriptionSync } from "@/lib/push/PushSubscriptionSync";
import { RealtimeGate } from "@/lib/realtime/RealtimeGate";
import { RealtimeIndicator } from "@/lib/realtime/RealtimeIndicator";
import { Building2, ChevronRight, Headset, LogOut, UserRoundCog, Undo2 } from "lucide-react";
import { Link, NavLink } from "react-router";
import "@altek/ui/components/layout/app-navbar.css";
import "@altek/ui/components/layout/app-sidebar.css";
import { PERSONA_LABEL, dashboardPersona } from "@/features/dashboard/lib/dashboard-persona";

const FieldQueueGate = lazy(() => import("@/features/operations/components/FieldQueueGate"));

const FAVOURITES_STORAGE_KEY = "altek.sidebar.favourites";

/**
 * Kişinin başlangıç ekranı olabilecek adresler — ürünün TEK panosu ve şoförün seferleri.
 *
 * ÜÇTÜ, BİRE İNDİ (16 Eylül 2026, kullanıcı kararı). «İK Panosu» ve «Yönetim Panosu» ayrı
 * ekranlardı ve bir İK uzmanının rayında iki pano birden duruyordu; hangisinin «asıl» olduğu
 * belli değildi. İçerikleri Panelim'in içinde birer BLOK oldu ve kişinin yetkisine göre
 * açılıyor.
 *
 * DİZİ OLARAK KALDI, tek elemanlı kaldığı sürede de. `pinnedDashboardId` rayda bulduğu SONUNCUYU
 * sabitliyor; diziyi tek bir sabite çevirmek, ikinci bir başlangıç ekranı gerektiğinde aynı kararı
 * yeniden almayı gerektirirdi — o gün geldi (aşağıda). `nav-catalog.ts` ile aynı değerler —
 * burada tekrarlanıyor çünkü rail'e ulaşan şey artık bir katalog girdisi değil, çözülmüş bir
 * satır.
 *
 * İKİNCİ ELEMAN «SEFERLERİM» (1 Kasım 2026): şoförün işi seferleri. Rayda Panelim'den SONRA geliyor,
 * yani «sonuncusu» kuralı onu seçiyor — şoför hesabı açılınca kök adres oraya gidiyor ve satır
 * FAVORİLER'in başına sabitleniyor. Taşımacının şoföründe Panelim'in akışı kapalı (`FeedPanelBlock`);
 * şirket şoförü Panelim'i rayda, bir tık ötede buluyor. Sistem yöneticisi `operations.self.*` taşımıyor (2 Kasım 2026): bütün
 * kataloğu aldığı için boş «Seferlerim»e düşüyordu, başlangıcı Panelim.
 */
const DASHBOARD_HREFS: readonly string[] = ["/my-panel", "/my-trips"];
/** Per person: `altek.sidebar.closedGroups:<userId>` — see `closedGroupIds`. */
const CLOSED_GROUPS_STORAGE_KEY = "altek.sidebar.closedGroups";
/**
 * CRITICAL, and only `SYSTEM_ADMIN` is seeded with it — `SECURITY_ADMIN` has it explicitly
 * removed. Keyed on the permission and not a role code, which a rename in the roles panel
 * would silently break.
 */
const IMPERSONATE_PERMISSION = "identity.user.impersonate";

/** Mobil gezinme çekmecesinin kimliği: menü düğmesi `aria-controls` ile ona işaret ediyor. */
const MOBILE_NAV_ID = "mobil-gezinme";

/** Every read is guarded: private browsing throws on the first access, not on write. */
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readStoredList(key: string): string[] | null {
  const raw = readStored(key);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((entry): entry is string => typeof entry === "string")
      : null;
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* the choice still applies for this session */
  }
}

/**
 * The authenticated shell: the kit's navbar across the top, the kit's rail down the side.
 *
 * Both come from `src/components/layout/` — the same components the catalogue publishes as the
 * ALTEK UI Kit, not a second pair maintained here. The shell's own job is small and is all
 * that lives in this file: turn the session into a rail model, remember what the person
 * collapsed and starred, and own the one `<main>`.
 */
/**
 * Kabuk, yuvanın açma bağlamının İÇİNDE: rayın canlı yardım düğmesi, sayfalardaki çağıranlar
 * (rehber çekmecesi) ve köşedeki yuva aynı `ChatDockProvider`'ı paylaşıyor (denetim Sohbet 18).
 */
export function AppLayout() {
  return (
    <ChatDockProvider>
      <AppLayoutShell />
    </ChatDockProvider>
  );
}

function AppLayoutShell() {
  const { status, session, permissions, reset, endedBy } = useAuth();
  const { preference, setPreference } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  /** Palet kapanınca odak açana (vazgeçildi) ya da `<main>`'e (sonuç seçildi) — `usePaletteFocusReturn`. */
  const paletteFocus = usePaletteFocusReturn(paletteOpen);
  /** Mac'te ⌘K, Windows ve Linux'ta Ctrl+K — tek dinleyici (`palette-shortcut.ts`; kitinki kapalı). */
  usePaletteShortcut(setPaletteOpen);
  /**
   * Paletin seçtiği KABUK KOMUTU («Kullanıcı değiştir»), palet kapandıktan SONRA çalışıyor.
   *
   * Komut bir pencere açıyor. Palet seçimle kapanırken odak geri veriliyor (`usePaletteFocusReturn`,
   * etkisi bundan önce yazılı ve önce çalışıyor); pencere aynı anda açılsaydı geri verme onun odağını
   * pencere sanıp ana içeriğe taşırdı. Sıra: palet kapanır, odak açana döner, pencere açılır.
   */
  const pendingCommand = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (paletteOpen) return;
    const run = pendingCommand.current;
    pendingCommand.current = null;
    run?.();
  }, [paletteOpen]);
  const [paletteTerm, setPaletteTerm] = useState("");
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  /**
   * RAY TERCİHLERİ KİŞİNİN, TARAYICININ DEĞİL (7 Ekim 2026). Favoriler ve kapalı gruplar tek bir
   * tarayıcı anahtarında duruyordu; aynı tarayıcıda oturum açan ikinci kişi — paylaşılan depo
   * bilgisayarı, ya da hesap değiştiren bir yönetici — öncekinin seçimlerini devralıyordu. Patron,
   * çalışanın kapattığı «İK», «Stok», «Pazarlama» gruplarını kapalı buluyordu. Anahtar artık kişi
   * kimliğini taşıyor; eski ortak anahtar yalnız favorilerde, bir kez, geçiş için okunuyor.
   */
  const userId = session?.user.id ?? null;
  const [favouriteIds, setFavouriteIds] = useState<string[]>([]);
  const [closedGroupIds, setClosedGroupIds] = useState<string[]>([]);
  useEffect(() => {
    if (userId === null) return;
    setFavouriteIds(
      readStoredList(`${FAVOURITES_STORAGE_KEY}:${userId}`) ??
        readStoredList(FAVOURITES_STORAGE_KEY) ??
        [],
    );
    // Kimlikler 8 Ekim 2026'dan beri boşluksuz (`domSafeGroupId`); eski kayıt da aynı biçime çevriliyor.
    setClosedGroupIds(
      (readStoredList(`${CLOSED_GROUPS_STORAGE_KEY}:${userId}`) ?? []).map(domSafeGroupId),
    );
  }, [userId]);

  /**
   * An administrator's own rail is the ADMINISTRATION, and nothing else.
   *
   * `SYSTEM_ADMIN` holds all 112 permissions, so a rail built honestly from what they may
   * open contains every employee and İK screen too — their own leave requests, their own
   * documents, the personnel list. That is technically true and practically useless: it
   * buries the twenty-seven screens they came for under thirty they did not, and none of the
   * employee screens even work for an account with no employee record behind it.
   *
   * So the areas are not merged for them. The way to see what somebody else's rail looks
   * like is to BECOME them — which is what `identity.user.impersonate` means, and why it is
   * the permission this is keyed on rather than a role code that a rename would break.
   * Everybody else keeps every area their grants reach: an İK specialist is also an employee,
   * and their own leave request belongs in their menu.
   */
  const canImpersonate = permissions.can(IMPERSONATE_PERMISSION);
  const ownArea: NavPanel | undefined = canImpersonate ? "admin" : undefined;

  /**
   * The menu this INSTALLATION offers, which is not the same list in both shapes.
   *
   * A dedicated installation has no Platform panel at all — one customer makes a customer list
   * meaningless, and its administrator is the customer's own IT manager rather than us. Read
   * from the session because it is the server's answer: the client cannot decide what shape of
   * installation it is talking to, and a build-time flag would be wrong the first time the same
   * bundle served both.
   *
   * `DEDICATED` while the session is still loading, which is the narrower of the two — a panel
   * that appears a moment late is a smaller error than one that flashes and disappears.
   */
  const installation = session?.tenant.mode ?? "DEDICATED";
  /**
   * ŞİRKET SATIRI ŞİRKETİN ADINI TAŞIR — «Şirket» genel bir kelime, bakılan şey BU şirket.
   *
   * Ad üç kaynaktan sırayla: tüzel kişilik (kurulumda kayıtlı gerçek ad — «İntraline İlaç
   * Sanayi ve Ticaret A.Ş.»), yoksa müşteri adı (ALTEK gibi), o da yoksa katalogun yedek
   * kelimesi. Katalog sabit bir dosya ve oturumu göremez; bu yüzden ad burada, çizim
   * aşamasında yerine konuyor.
   */
  /**
   * Şirket satırı yalnız dört sekmeden BİRİNE yetkisi olanda çiziliyor — rotanın kapısıyla aynı
   * birleşim (`router.tsx`, `/company`). Menüde satırı olmadığı için bu kontrol burada duruyor:
   * rayın alt bandı katalogdan geçmiyor.
   */
  const companyHref =
    permissions.can("organization.org_unit.read") ||
    permissions.can("documents.company.read") ||
    permissions.can("partners.partner.read") ||
    permissions.can("hr.document.read")
      ? "/company"
      : null;

  const companyName =
    session?.scopes.legalEntities[0]?.name ??
    session?.tenant.current?.displayName ??
    session?.tenant.current?.name ??
    COMPANY_GROUP_TITLE;

  const catalog = useMemo(
    () => navCatalogFor(installation, permissions),
    [installation, permissions],
  );

  const groups = useMemo(
    () =>
      // Boşluksuz kimlik: `aria-controls` bir kimlik listesi (`withDomSafeIds`, kit isteği raporda).
      withDomSafeIds(
        buildSidebarGroups({
          catalog,
          areas: NAV_PANELS,
          /**
           * Without this the personal toolbox disappears the moment the rail is narrowed to the
           * admin area — the "modüller gelmemiş" report. The kit can no longer know which panel
           * is personal, and should not: that is the product's answer.
           */
          alwaysVisibleAreas: ALWAYS_VISIBLE_PANELS,
          can: permissions.can,
          canForOthers: permissions.canForOthers,
          isModuleEnabled: permissions.isModuleEnabled,
          ...(ownArea ? { area: ownArea } : {}),
        }),
      ),
    [catalog, permissions, ownArea],
  );

  /**
   * Çalışma alanı her zaman EN ALTTA.
   *
   * `NAV_PANELS` sırası değiştirilmedi ve bu kasıtlı: o sıra yalnız rayı çizmiyor, aynı
   * zamanda «kişinin ulaşabildiği en dar alan» hesabını besliyor (`areaLabelFor` listeyi
   * TERSTEN tarayıp ilk görüneni döndürüyor, benim sabit pano seçimim de sonuncuyu alıyor).
   * Çalışma alanını o listede sona taşımak, herkesin alan adını «Çalışma Alanı» yapardı ve
   * İK uzmanına «İK» yerine onu yazardı.
   *
   * Sıralama bu yüzden ÇİZİM aşamasında yapılıyor: kişisel araç kutusu — sohbet, notlar,
   * dönüştürücü — kimsenin işi değil, işin yanında duran şey. Menünün başında durduğunda
   * insanın aradığı iş ekranını aşağı itiyordu.
   */
  const orderedGroups = useMemo(
    () => [
      ...groups.filter((group) => !group.id.startsWith("workspace:")),
      ...groups.filter((group) => group.id.startsWith("workspace:")),
    ],
    [groups],
  );

  /**
   * Kişinin KENDİ panosu, her zaman favorilerin başında ve çıkarılamaz.
   *
   * NEDEN SABİT. «Panolar» bölümü kaldırıldı (bkz. `nav-catalog.ts`), yani panoya giden kısa
   * yol artık favoriler listesi. Çıkarılabilir olsaydı, bir kere yanlışlıkla yıldızı kapatan
   * kişi kendi panosuna giden kısayolu kalıcı olarak kaybederdi — ve onu geri getirmenin yolu,
   * panoyu kendi bölümünde bulup yeniden yıldızlamak olurdu, yani tam da kısayolun var olma
   * sebebini yapmak.
   *
   * HANGİSİ OLDUĞU RAYIN KENDİSİNDEN ÇIKARSANIYOR, ayrı bir liste tutulmuyor: rail zaten
   * kişinin görebildiği alanlara göre kurulmuş durumda ve panolar her alanın İLK grubu. Ayrı
   * bir «kimin panosu hangisi» tablosu tutmak, iki kaynağın ayrışabileceği bir yer daha açardı.
   */
  /**
   * Kurulumun kendi görünüşü. Oturum yüklenmeden önce ürünün kendi adı — bir kare boyunca
   * boş bir başlık göstermek, yanlış bir ad göstermekten kötü değil ama daha çirkin.
   */
  const appearance = session?.appearance ?? {
    markUrl: null,
    // Ürünün adı DEĞİL kurulumunki (24 Ekim 2026): oturum gelene kadarki o tek kare de
    // kullanıcının gördüğü bir kare. Sabit «ALTEK ERP» burada bir an için yanlış kurulumun
    // adını yazıyordu; `core.brand.product_name` geldiğinde zaten onun yerini alıyor.
    productName: env.brandName,
    supportContactEmail: null,
    supportUserId: null,
  };
  const supportUserId = appearance.supportUserId;

  /**
   * Canlı yardım: köşedeki yuvayı destek kişisiyle bir TASLAKLA açıyor (`ChatDockProvider`).
   *
   * Konuşma basınca değil ilk mesajla oluşuyor; o kişiyle zaten bir konuşma varsa yuva, listeyi
   * elde ettikten sonra onu seçiyor. Destek kişisi tanımlı değilse düğme HİÇ çizilmiyor
   * (8 Ekim 2026; eskiden sıradan kişi seçicisi açılıyordu — gerekçe düğmenin yanında). Sohbet
   * sayfasındayken aynı istek sayfanın kendi `?with=` adresine çevriliyor.
   */
  const chatDock = useChatDock();
  /**
   * MÜŞTERİ SEÇİLMEDEN KABUK SORGUSU GİTMEZ (http-çekirdek R5).
   *
   * Aşağıda `needsTenantChoice(session)` ise ekran yerine müşteri seçici çiziliyor — ama bu iki
   * sorgu o karardan ÖNCE kuruluyor ve müşteri kapsamlı her rota o durumda 409
   * `TENANT_SELECTION_REQUIRED` dönüyor. Yani seçici çizilirken arkada iki istek boşuna gidip
   * hata sayacını şişiriyordu; ekranda görünmediği için kimse fark etmiyordu.
   */
  const shellQueriesEnabled =
    session !== undefined && !needsTenantChoice(session) && !mustChangePassword(session);
  /**
   * KUTUSU OLMAYAN HESAP (1 Kasım 2026). Zil, onay kutusu ve profil kartı her hesabın bir iç hesap
   * olduğunu varsayıyordu; taşımacının şoförü yalnız «Şoför» rolüyle giriyor ve bu üç anahtarın
   * hiçbirini taşımıyor — her ekranda üç 403. Anahtarı olmayan soru artık gitmiyor. Kit iki simgeyi
   * yine çiziyor (gizleme yuvası yok, kit isteği); paneller boş durumlarını söylüyor.
   */
  const canReadNotifications = permissions.can("notifications.notification.read");
  const canReadApprovals = permissions.can("workflow.approval.read");
  /** Zilin rozeti. Sayfa değil, kabuk sorusu — her ekranda aynı sayı görünmeli. */
  const notificationUnread =
    useQuery({ ...unreadCountQuery(), enabled: shellQueriesEnabled && canReadNotifications }).data
      ?.unread ?? 0;
  /**
   * Onay sayısı, PANELİN OKUDUĞU SORGUNUN AYNISINDAN — ayrı bir uç değil.
   *
   * Aynı `queryKey`, yani React Query ikisini tek çağrıda birleştiriyor ve rozetteki sayı
   * ile panelde görünen satırlar aynı andan geliyor. İki ayrı çağrı, rozette 3 yazıp
   * listede 2 satır göstermenin kısa yoluydu.
   */
  const approvalInbox = useQuery({
    // En acil iş üstte (9 Ekim 2026): sunucunun varsayılanı `desc`, kutu en geç vadeliyi gösteriyordu.
    // Anahtar `ApprovalsPanel` ile ORTAK — ikisi aynı parametrelerle sorulmalı.
    ...approvalInboxQuery({ page: 1, limit: 5, sortBy: "dueAt", sortOrder: "asc" }),
    enabled: shellQueriesEnabled && canReadApprovals,
  });
  const approvalPending = approvalInbox.data?.pagination.total ?? 0;

  const pinnedDashboardId = useMemo<string | null>(() => {
    /**
     * SONUNCUSU, ilki değil.
     *
     * Rail, kişinin ulaşabildiği alanları `NAV_PANELS` sırasıyla diziyor ve o sıra en geniş
     * kitleden en dara gidiyor: çalışma alanı → çalışan → İK → … → yönetim. Bir İK uzmanının
     * rayında iki pano birden var — «Panelim» ve «İK Panosu» — ve ilkini sabitlemek herkese
     * aynı panoyu, yani en genelini vermek olurdu. Sonuncusu, kişinin kendi işine en yakın
     * olanı: İK uzmanına İK panosu, sistem yöneticisine yönetim panosu, çalışana kendi paneli.
     */
    let found: string | null = null;
    for (const group of groups) {
      for (const item of group.items) {
        if (DASHBOARD_HREFS.includes(item.id)) found = item.id;
      }
    }
    return found;
  }, [groups]);

  /**
   * Everything open unless the person closed it. What is remembered is the CLOSED set, not the
   * open one: a group that appears later — a new role, a module switched on — opens by default
   * instead of arriving closed because it was missing from an old list.
   */
  const openGroupIds = useMemo(
    () => openGroupsFrom(groups, closedGroupIds),
    [groups, closedGroupIds],
  );

  /**
   * KABUĞUN ŞİRKETİ, EKRANLARIN ŞİRKETİ (9 Ekim 2026). Burada kendi `useState`'i ve bellekten
   * okuması vardı; ekranlar ise `useScope()`'tan — adres (`?company=`) kazanarak — okuyordu. Seçici
   * yalnız kabuğun kopyasını değiştiriyordu ve açık ekran eski şirkette kalıyordu. Tek kaynak
   * `useScope`; seçicinin kendisi `CompanySlot`'ta. Okunamayan bir şirketi adreste ya da bellekte
   * bulan `useScope` zaten ilk okunabilir şirkete düşüyor.
   */
  const scope = useScope();
  const legalEntityId = scope.legalEntityId;

  /**
   * KONGRELER DE ROZETTE (7 Ekim 2026, ikinci inceleme turu). Kongre onayı bir iş akışı görevi
   * değil; kutu onları ayrı bir satırda gösteriyor (`ApprovalsPanel`) ama rozet yalnız iş akışını
   * sayıyordu — dört kongre bekleyen yöneticinin simgesinde sayı yoktu. Aynı sorgu anahtarı,
   * yani kutuyla tek istek.
   */
  const canApproveEvents =
    // Yalnız yönetim rolünden gelen yetki kongre kuyruğuna sayılmaz (20 Ekim 2026).
    decidesForOthers(permissions, "events.event.approve") &&
    permissions.isModuleEnabled("module.events");
  const plannedEvents = useQuery({
    ...eventListQuery(legalEntityId, { status: "PLANNED", limit: 1 }),
    enabled: shellQueriesEnabled && canApproveEvents && legalEntityId !== null,
  });
  const approvalBadge =
    approvalPending + (canApproveEvents ? (plannedEvents.data?.pagination.total ?? 0) : 0);

  /**
   * Back to the administrator's own identity.
   *
   * Two calls, in this order and not the other. Refreshing FIRST restores their own token;
   * only then may the support window be closed, because while impersonating the request
   * resolves against the TARGET, and the person whose account was entered does not hold
   * `identity.session.revoke`. Closing is best-effort: if it fails the window still expires by
   * itself, and the thing that actually matters — whose token the browser holds — is already
   * done.
   */
  const returnToSelf = useMutation({
    mutationFn: async () => {
      await adminApi.impersonation.returnToSelf();
      let windowId: string | null = null;
      try {
        windowId = sessionStorage.getItem(IMPERSONATION_WINDOW_KEY);
        sessionStorage.removeItem(IMPERSONATION_WINDOW_KEY);
      } catch {
        /* the window expires on its own */
      }
      if (windowId) {
        try {
          await adminApi.impersonation.end(windowId);
        } catch {
          /* already ended, or expired — the token swap above is what mattered */
        }
      }
    },
    onSettled: () => {
      /**
       * KENDİNE DÖNEN YÖNETİCİ, KENDİ PANOSUNA GİDER.
       *
       * Eskiden `/admin`'e gidiyordu ve o gün doğruydu: yönetici için «Yönetim Panosu» vardı.
       * Pano 16 Eylül 2026'da kaldırıldı ve içeriği Panelim'in yönetim bloğu oldu; bu satır
       * güncellenmeseydi kimliğe geri dönen yönetici 404 görürdü — ve tam o anda, hangi
       * hesapta olduğundan emin olması gereken anda. Sayfa temiz yüklenir (`reloadIntoIdentity`).
       */
      reloadIntoIdentity("/my-panel");
    },
  });

  /**
   * Rayın alt bandı, profil menüsü ve engelleyen müşteri seçicisi aynı çıkışı kullanıyor —
   * gerekçeleri ve «Çıkış yaptınız» notu `useSignOut`'ta (9 Ekim 2026, 5. tur).
   */
  const signOut = useSignOut();

  // Closes the mobile drawer on navigation. Without this the drawer stays open over the page
  // the user just asked for, and they have to dismiss it to see what they chose.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  /**
   * ÇEKMECENİN KLAVYE SÖZLEŞMESİ (8 Ekim 2026): açılınca odak içeri, Tab içeride döner, Escape
   * kapatır, kapanınca odak menü düğmesine döner — gerekçesi `useModalSheet`'te.
   */
  const mobileNavRef = useRef<HTMLDivElement | null>(null);
  useModalSheet({
    open: mobileNavOpen,
    onClose: () => setMobileNavOpen(false),
    panel: mobileNavRef,
  });

  /**
   * Pencere 1024 pikselin üstüne genişlerse çekmece kapanır: orada ray sabit ve çekmece CSS ile
   * gizli (`lg:hidden`) — açık kalsaydı odak görünmeyen bir panelin içinde tutulurdu. Eşik bir
   * medya sorgusu, kabuğun `lg` kırılımıyla aynı — ortak kancadan (`src/hooks/useMediaQuery.ts`).
   */
  const wideShell = useMediaQuery(MEDIA.wideShell);
  useEffect(() => {
    if (mobileNavOpen && wideShell) setMobileNavOpen(false);
  }, [mobileNavOpen, wideShell]);

  /**
   * Telefonda arama, kitin telefon kuralıyla simgelerin yanına yaslanıyor (`searchAlign` yok).
   * `end` 768 pikselin altında da 28rem'lik bir esnek bölge istiyordu: logo 9 piksele ezildi,
   * büyüteç çubuğun ortasında kaldı (8 Ekim 2026 ölçümü, 375 px). Eşik ortak kancadan; kabuk artık
   * sohbetin kancasını (`features/chat`) içe aktarmıyor.
   */
  const narrowScreen = useMediaQuery(MEDIA.phone);

  /**
   * Menu rows plus the personal screens that live on Panelim tiles (`global-search.ts`): the
   * short menu stays short, but «izin» typed into the search finds «İzin taleplerim».
   */
  // Personal screens only with an ACTIVE employment — the same rule Panelim uses for its tiles.
  const hasEmployee = session?.employment != null;
  /**
   * KİŞİNİN GİREBİLDİĞİ BÖLÜMLER — paletin bölüm ekranlarını bununla eliyor (24 Ekim 2026).
   *
   * Kaynak `groups`, yani kenar çubuğunun ÇİZDİĞİ satırlar: palet ile menü böylece aynı cevabı
   * veriyor ve bölümün kapısı değiştiğinde palet kendiliğinden uyuyor. Kapıyı buraya kopyalamak
   * — «bölüm X şu anahtarı ister» diye ikinci bir liste tutmak — bu hatanın ta kendisiydi.
   */
  const visibleSections = useMemo(
    () => new Set(groups.flatMap((group) => group.items.map((item) => item.href))),
    [groups],
  );

  const paletteScreens = useMemo(() => {
    /**
     * Ekran satırı menüdeki simgesiyle (1 Kasım 2026): palet on dört ekranı aynı genel simgeyle
     * diziyordu ve satırı menüdeki karşılığıyla eşleştirmek yalnız ada kalıyordu. Rayın simgesi
     * çizilmiş bir öğe; paletin istediği bileşen, kataloğun kendisinden.
     */
    const iconOf = new Map(catalog.flatMap((group) => group.items.map((item) => [item.to, item.icon])));
    const menu = groups.flatMap((group) =>
      group.items.map((item) => ({
        id: `ekran:${item.href}`,
        label: item.label,
        description: group.label,
        icon: iconOf.get(item.href),
        keywords: "",
      })),
    );
    const menuHrefs = new Set(menu.map((item) => item.id));
    const sections = SECTION_SCREENS.filter(
      (screen) =>
        screenAllowed(screen, permissions, true, visibleSections) &&
        !menuHrefs.has(`ekran:${screen.href}`),
    ).map((screen) => ({
      id: `ekran:${screen.href}`,
      label: screen.label,
      description: screen.description,
      keywords: screen.keywords,
    }));
    const personal = PERSONAL_SCREENS.filter(
      (screen) =>
        screenAllowed(screen, permissions, hasEmployee) && !menuHrefs.has(`ekran:${screen.href}`),
    ).map((screen) => ({
      id: `ekran:${screen.href}`,
      label: screen.label,
      description: screen.description,
      keywords: screen.keywords,
    }));
    return [...menu, ...sections, ...personal];
  }, [catalog, groups, permissions, hasEmployee, visibleSections]);

  /**
   * HIZLI İŞLEMLER (1 Kasım 2026) — kataloğu `quick-actions.ts`. Kişinin yapabildikleri, ve boş
   * palet için rolünün ilk işleri (Panelim'in persona'sı: depo müdürüne mal kabul, mümessile hekim
   * görüşmesi, sistem yöneticisine kullanıcı daveti).
   */
  const impersonating = session?.impersonation != null;
  const paletteActions = useMemo(() => {
    const allowed = quickActionsFor({
      permissions,
      hasEmployee,
      visibleSections,
      hasCompany: legalEntityId !== null,
      impersonating,
    });
    const toItem = (action: QuickAction) => ({
      id: quickActionItemId(action),
      label: action.label,
      description: action.description,
      icon: action.icon,
      keywords: action.keywords,
    });
    return {
      all: allowed.map(toItem),
      featured: featuredQuickActions(allowed, dashboardPersona(permissions)).map(toItem),
    };
  }, [permissions, hasEmployee, visibleSections, legalEntityId, impersonating]);

  /**
   * The person's own card — the name and title they chose in «Profilim». The session carries a
   * name only for accounts with a personnel file; a role account (patron, a department login)
   * showed its e-mail address as its name, and the employee's line under the name was a MENU
   * GROUP («Çalışma Alanı»), not what they do.
   */
  const ownProfile = useQuery({
    ...myProfileQuery(),
    // Parola değişene kadar sunucu yalnız değiştirme ekranının uçlarını açıyor (31 Ekim 2026).
    // Profil anahtarı olmayan hesap (taşımacının şoförü, 1 Kasım 2026) e-postasıyla görünür.
    enabled:
      status === "authenticated" &&
      !(session !== undefined && mustChangePassword(session)) &&
      permissions.can("directory.profile.manage"),
    retry: false,
  });

  const recordSearch = useRecordSearch({
    term: paletteTerm,
    permissions,
    legalEntityId,
    enabled: paletteOpen,
  });

  /** Aramanın kapıları — yer tutucu, kayıt kaynakları ve karekod eylemi aynı cevaptan. */
  const searchSources = useMemo(() => recordSearchSources(permissions), [permissions]);

  const paletteGroups = useMemo<CommandGroup[]>(() => {
    const strip = ({ keywords: _keywords, ...item }: CommandItem & { keywords: string }) => item;
    const pick = (items: Array<CommandItem & { keywords: string }>) =>
      items
        .filter((item) => matchesTerm(paletteTerm, item.label, item.description, item.keywords))
        .map(strip);
    const screens = pick(paletteScreens);
    /**
     * Boş palet: rolün ilk işleri, sonra ekranlar. Yazınca bütün işlemler ekranlarla birlikte
     * süzülüyor; terimden doğan işlemler en başta — taranan karekod («Karekodu sorgula») ve plakası
     * yazılan aracın işi («İade al · 34 ABC 123»).
     */
    const actions = paletteTerm.trim()
      ? [
          ...codeLookupActions(paletteTerm, searchSources),
          ...recordSearch.actions,
          ...pick(paletteActions.all),
        ]
      : paletteActions.featured.map(strip);
    return [
      ...(actions.length > 0 ? [{ id: "hizli-islemler", label: "Hızlı işlemler", items: actions }] : []),
      ...(screens.length > 0 ? [{ id: "ekranlar", label: "Ekranlar", items: screens }] : []),
      ...recordSearch.groups,
    ];
  }, [paletteScreens, paletteActions, paletteTerm, recordSearch.groups, recordSearch.actions, searchSources]);

  /** Paletin yer tutucusu, bu kişinin ARAYABİLDİĞİ şey — kayıt aramasıyla aynı kapılardan. */
  const searchPlaceholder = useMemo(() => searchPlaceholderFor(searchSources), [searchSources]);

  /**
   * SEKME BAŞLIĞI — «Notlarım · ALTEK ERP» (8 Ekim 2026, WCAG 2.4.2; gerekçe `document-title.ts`).
   *
   * Ad kişinin GÖRDÜĞÜ satırlardan: ray, yetkili olduğu sekme ve kişisel ekranlar (aramanın
   * listeleri), menü dışı kabuk ekranları ve şirket satırı. Bir ekran adı düzeltebiliyor
   * (`useScreenTitle` → `titleOverride`); «Sayfa bulunamadı» bunu yapıyor.
   */
  const titledPaths = useMemo<TitledPath[]>(
    () => [
      ...titledPathsOf(orderedGroups),
      // Sekme başlığı da aynı kapıdan: adını yazdığımız ekran, kişinin GİREBİLDİĞİ ekran olmalı —
      // «Sayfa bulunamadı»nın sakladığını başlıkta söylemek, onu paletinde söylemekle aynı şey.
      ...SECTION_SCREENS.filter((screen) =>
        screenAllowed(screen, permissions, true, visibleSections),
      ),
      ...personalScreenTitles(permissions),
      ...OFF_MENU_TITLES,
      ...(companyHref ? [{ href: companyHref, label: companyName }] : []),
    ],
    [orderedGroups, permissions, companyHref, companyName, visibleSections],
  );
  const [titleOverride, setTitleOverride] = useState<string | null>(null);
  const screenTitle = titleOverride ?? screenTitleFor(location.pathname, titledPaths);
  const productName = appearance.productName;
  useEffect(() => {
    document.title = withKeptPrefix(document.title, composeDocumentTitle(screenTitle, productName));
  }, [screenTitle, productName]);

  /**
   * STILL READING THE SESSION IS NOT THE SAME AS HAVING NONE — and conflating them cost every
   * bookmark and every shared link in the product.
   *
   * On a cold load of any deep path this component rendered before the session query had
   * answered, so `session` was `undefined` and this line bounced to `/login`. `LoginPage` then
   * saw the session arrive, concluded the visitor was already signed in, and sent them to
   * `/my-panel`. The path they actually asked for was gone, replaced twice over — and nothing
   * looked broken, which is why it survived: you simply end up on your dashboard and assume you
   * mistyped the address. Measured in the browser: `/admin/departments` opened cold, a screen
   * this very user may open, landed on `/my-panel`.
   *
   * So the three states are answered separately, as `ProtectedRoute` already does:
   * loading waits, unauthenticated goes to sign-in CARRYING where it was going, and only then
   * does the shell render.
   */
  if (status === "loading") {
    return (
      <div className="grid min-h-dvh place-items-center bg-bg-app">
        <div className="w-full max-w-72 space-y-3" aria-busy="true" aria-label="Yükleniyor">
          <SkBlock className="mx-auto h-6 w-40" />
          <SkLine w="100%" />
          <SkLine w="70%" />
        </div>
      </div>
    );
  }

  /**
   * No session — go to sign-in, never render nothing.
   *
   * This used to `return null`, which produced a genuinely blank white page for any route
   * under this layout that was not itself wrapped in `ProtectedRoute`: the catch-all 404 and
   * the developer surfaces. A blank page is the worst possible answer, because it looks like
   * the application is broken rather than like the user is signed out.
   *
   * `state.from` is what makes signing in return somebody to the link they followed instead of
   * to the dashboard. It was missing here while `ProtectedRoute` carried it — and this is the
   * branch that actually fires on a cold load, so the one that carried it never ran.
   */
  /**
   * Kök adres, kişinin KENDİ panosuna gider — herkese `/my-panel` değil.
   *
   * Sistem yöneticisinin çalışan paneli yok: hesabın arkasında bir personel kaydı bulunmadığı
   * için «Panelim» ona boş bir izin bakiyesi ve boş bir belge listesi gösteriyordu. Giriş
   * yaptığı ilk ekranın, işini yapamadığı bir ekran olması iyi bir karşılama değil.
   *
   * Hedef, rayın kendisinden okunuyor (`pinnedDashboardId`) — ayrı bir "kim nereye gider"
   * tablosu tutmuyoruz, çünkü o tablo menüyle ayrışabilirdi.
   */
  if (session && location.pathname === "/" && pinnedDashboardId !== null) {
    return <Navigate to={pinnedDashboardId} replace />;
  }

  if (!session) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search, expired: endedBy === "expired" }}
      />
    );
  }

  const toggleFavourite = (id: string): void => {
    /**
     * Sabit pano artık `favouriteIds`'e hiç yazılmıyor: kit `lockedFavouriteIds` ile onu
     * listenin başına kendisi koyuyor, kendi bölümünden düşürüyor ve yıldızını çizmiyor.
     * Buradaki koruma yine de duruyor — kişinin saklanmış listesinde eski bir kayıt varsa
     * onu depolamaya geri yazmayalım.
     */
    if (id === pinnedDashboardId) return;
    setFavouriteIds((current) => {
      const next = current.includes(id)
        ? current.filter((entry) => entry !== id)
        : [...current, id];
      if (userId !== null) writeStored(`${FAVOURITES_STORAGE_KEY}:${userId}`, JSON.stringify(next));
      return next;
    });
  };

  const changeOpenGroups = (ids: string[]): void => {
    const closed = closedGroupsFrom(groups, ids);
    setClosedGroupIds(closed);
    if (userId !== null)
      writeStored(`${CLOSED_GROUPS_STORAGE_KEY}:${userId}`, JSON.stringify(closed));
  };

  const impersonation = session.impersonation;
  const card = ownProfile.data?.card;
  const persona = dashboardPersona(permissions);
  const personaLabel = PERSONA_LABEL[persona];
  /**
   * NAME: what the person chose, else the personnel file, else — for a role account with
   * neither — the role itself («Patron»), so the header does not read like a mail client.
   */
  // The card falls back to the e-mail or its local part («patron») when nobody chose a name;
  // neither is a name, and «patron / Patron» in the header read like a typo.
  const localPart = session.user.email.split("@")[0] ?? "";
  const cardName =
    card &&
    card.displayName !== session.user.email &&
    card.displayName.toLocaleLowerCase("tr") !== localPart.toLocaleLowerCase("tr")
      ? card.displayName
      : null;
  const personName =
    cardName ?? session.employee?.displayName ?? personaLabel ?? session.user.email;
  /**
   * THE LINE UNDER IT: their own headline, job title or unit; for a manager-type persona the
   * role. Never a menu group name, and never the name repeated («Patron / Patron»).
   */
  const subtitle =
    card?.headline ??
    card?.jobTitle ??
    /* Mümessil (`field`, 30 Ekim 2026) çalışan gibi: rol etiketi yok, birimi yazıyor. */
    (persona === "employee" || persona === "field" ? card?.orgUnit?.name : personaLabel) ??
    "";
  const person = {
    name: personName,
    roleLabel: subtitle === personName ? "" : subtitle,
    email: session.user.email,
    /**
     * SATICININ KENDİ HESABI ALTEK MARKASINI TAŞIYOR.
     *
     * Kim olduğu tahmin edilmiyor, ÖLÇÜLÜYOR: `platform.*` anahtarları hiçbir müşteri rolüne
     * verilemiyor (`VENDOR_PERMS`) ve yalnız bir betikle, tek tek hesaplara yazılıyor. Yani
     * bu anahtarı taşımak "bu hesap ALTEK'in" demenin kendisi; ayrıca bir bayrak eklemek,
     * ayrışabilecek ikinci bir kaynak olurdu.
     *
     * Yalnız OTURUM AÇMIŞ hesap için: başka birinin kartında hangi anahtarları taşıdığı
     * bilinmiyor ve sunucu da söylemiyor. Herkes için olması gerekirse bunun cevabı kişi
     * kartında bir alan; o ayrı bir karar.
     *
     * PNG, SVG DEĞİL. `favicon.svg` aynı markayı taşıyor ama avatar `<img>` içinde kırık
     * göründü: kit görseli `object-cover` ile dolduruyor ve iç ölçüsü olmayan bir SVG o kutuda
     * güvenilir ölçeklenmiyor. Raster'da böyle bir soru yok.
     */
    ...(permissions.can("platform.tenant.read") || permissions.can("platform.billing.read")
      ? { src: "/icon-192.png" }
      : {}),
  };

  /**
   * The rail refuses to import the router, so the adapter lives here. `data-rail-item` is
   * forwarded rather than dropped: it is how the rail's Up/Down keys find the next row.
   */
  /**
   * `Link`, NOT `NavLink` (7 Ekim 2026, kullanılabilirlik turu). `NavLink` kendi ön ek eşleşmesiyle
   * `aria-current="page"` yazıyordu: «/events/plans» açıkken hem «Yıllık Plan» hem «Kongre ve
   * Etkinlik» seçili görünüyordu. Hangi satırın etkin olduğuna kit karar veriyor
   * (`activeHrefFor`, en uzun ön ek) ve özniteliği `rest` içinde yolluyor; bağlantı onu olduğu gibi
   * taşımalı, üstüne kendi kararını yazmamalı.
   */
  const renderLink = ({ href, children, ref, className, style, ...rest }: SidebarLinkProps) => (
    <Link
      {...rest}
      {...(className === undefined ? {} : { className })}
      {...(style === undefined ? {} : { style })}
      to={href}
      ref={ref}
    >
      {children}
    </Link>
  );

  /**
   * Sohbet açık mı — hem modül hem yetki. Kapalıysa canlı yardım düğmesi HİÇ çizilmiyor.
   * Köşedeki düğmenin ve `<main>` rezervinin okuduğu kuralın aynısı (`chat-launcher-visibility`).
   */
  const chatAvailable = chatReadable(permissions);

  /** Kararın kendisi ve gerekçesi `settings-row.ts`'te — çiti olsun diye orada. */
  const settingsHref = settingsHrefFor(orderedGroups);

  const rail = (variant: "fixed" | "sheet") => (
    <AppSidebar
      variant={variant}
      groups={orderedGroups}
      activeHref={activeHrefFor(orderedGroups, location.pathname)}
      openGroupIds={openGroupIds}
      onOpenGroupsChange={changeOpenGroups}
      favouriteIds={favouriteIds}
      lockedFavouriteIds={pinnedDashboardId === null ? [] : [pinnedDashboardId]}
      onToggleFavourite={toggleFavourite}
      renderLink={renderLink}
      /**
       * ÇEKMECEDE ŞİRKET SEÇİCİ (9 Ekim 2026). Kit üst çubuğun şirket yuvasını 1024 pikselin altında
       * gizliyor (`.navbar-company`); birden çok şirket okuyan kişi tablette ve telefonda hangi
       * şirkette olduğunu göremiyor, değiştiremiyordu. `admin-shell.md` › Mobile: «The scope
       * selector moves into the drawer header». Tek şirkette gerek yok — ad rayın alt bandında.
       */
      {...(variant === "sheet" && scope.canSwitch ? { header: <CompanySlot block /> } : {})}
      /**
       * RAYIN ALT BANDI: çıkış ve canlı yardım.
       *
       * İkisi de menüde bir SATIR değil, çünkü ikisi de gezinme değil. Çıkış, listenin içinde
       * dururken yanlışlıkla basılabilecek bir şey; altta, sabit ve tek başına durduğunda ne
       * olduğu belli. Canlı yardım da bir ekrana gitmiyor — sohbeti sistem yöneticisiyle
       * açıyor, yani bir eylem.
       *
       * Rayın en altında SABİT: kit'in üç bölgesinden biri zaten burası ve yalnız ortadaki
       * kayıyor. Yani uzun bir menüde aşağı kaydırmak çıkışı gözden kaybettirmiyor.
       */
      footer={
        <div className="flex flex-col gap-1">
          {/*
            ŞİRKETİN KENDİSİ — KENDİ BÖLÜMÜ, rayın en altında ve çıkışın hemen üstünde.

            Menüde bir satır DEĞİL, ve bu bilinçli: gezindiğin ekranlarla aynı listede durursa
            «Çalışma alanı»nın bir parçası gibi okunuyordu (bir önceki hâli buydu). Alt bant
            kitin üç bölgesinden biri ve tek sabit olanı — uzun bir menüde aşağı kaydırmak onu
            gözden kaybettirmiyor. Favori yıldızı da burada yok, çünkü favori bir MENÜ satırının
            kolu; burası menü değil.

            Adı oturumdan: tüzel kişilik varsa onun adı («İntraline İlaç Sanayi ve Ticaret
            A.Ş.»), yoksa müşteri adı. Uzun adlar kırpılıyor — ray 240 piksel ve ad bir cümle
            uzunluğunda olabiliyor; tam hâli `title` ile duruyor.
          */}
          {companyHref ? (
            <NavLink
              to={companyHref}
              title={companyName}
              className={({ isActive }) =>
                [
                  /*
                    Yalnız VAR OLAN kabuk jetonları: `--color-shell-bg-hover`, `--color-shell-fg`
                    ve `--color-shell-fg-active` tanımlı (`tokens.css`). Olmayan bir sınıf adı
                    (uydurduğum `bg-shell-active` gibi) sessizce hiçbir şey yapardı — bu dosyada
                    bugün aynı tuzağa `inset-block-0` ile bir kez düşüldü.
                  */
                  "flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-semibold",
                  /*
                    ÇEKMECEDE 44 PİKSEL (8 Ekim 2026): satır 28 pikseldi, telefonda başparmağın
                    güvenilir vurduğu en küçük hedefin altında (responsive-and-mobile.md HARD
                    RULE). Kitin ray satırları da çekmecede 44'e çıkıyor; alt bant onlarla aynı.
                  */
                  variant === "sheet" ? "min-h-11" : "",
                  isActive ? "bg-shell-bg-hover text-shell-fg-active" : "text-shell-fg",
                ].join(" ")
              }
            >
              <Building2 size={16} strokeWidth={2} aria-hidden="true" className="shrink-0" />
              <span className="truncate">{companyName}</span>
              {/*
                KÜÇÜK OK — «buraya basılır» demenin hover'sız yolu.

                Satır bir menü listesinin içinde değil, alt bantta tek başına duruyor; oradaki
                bir yazı, tıklanabilir olduğunu kendiliğinden anlatmıyor. İmleçle beliren bir
                işaret bu kurulumda zaten yasak (kitin hover kuralı) ve tablette hiç görünmezdi,
                o yüzden ok HER ZAMAN duruyor.

                `ml-auto`: ad kısaldığında ok sağ kenarda kalıyor, adın peşine takılmıyor.
              */}
              <ChevronRight
                size={14}
                strokeWidth={2}
                aria-hidden="true"
                className="ml-auto shrink-0 opacity-70"
              />
            </NavLink>
          ) : null}

          {/*
            ŞİRKET SATIRI İLE ÇIKIŞ ARASINDAKİ ÇİZGİ — marka renginde, jetondan.

            `--brand-on-dark` (#57BAD7) kabuğun kendi marka rengi: aktif menü göstergesi de o.
            Kullanıcının seçtiği renk buydu (bir hane farkla yazılmıştı) ve zaten tanımlı bir
            jeton olduğu için koyu temada da doğru kalıyor — sabit bir hex yazmak, temanın
            değiştiği anda yanlış renge dönerdi.

            Çizgi ÜSTTE değil ALTTA duruyor: şirket satırını çıkıştan ayırıyor, yani ikisinin
            ayrı şeyler olduğunu söylüyor. Kitin kendi ayracı bandın tamamının üstünde zaten var.
          */}
          <div className="flex items-center gap-2 border-t border-brand-on-dark pt-1.5">
            <Button
              variant="quiet"
              size="sm"
              // Çekmecede 44 piksel — yukarıdaki şirket satırıyla aynı gerekçe.
              className={
                variant === "sheet" ? "min-h-11 flex-1 justify-start" : "flex-1 justify-start"
              }
              onClick={() => signOut.mutate()}
              disabled={signOut.isPending}
            >
              <LogOut size={16} strokeWidth={2} aria-hidden="true" />
              Çıkış yap
            </Button>
            {/*
            CANLI YARDIM — destek kişisiyle konuşmayı DOĞRUDAN açıyor.

            Bir süre yalnız yeni konuşma kipini açıyordu, çünkü kurulum «canlı yardıma kim
            bakıyor» sorusunun cevabını hiçbir yerde tutmuyordu. Artık tutuyor:
            `core.support.contact_email` ayarı var ve sunucu onu oturum yanıtında BİR HESAP
            KİMLİĞİNE çevirerek veriyor (`appearance.supportUserId`).

            Kimliği sunucunun çözmesi bir kolaylık değil, doğruluk meselesi: istemci e-postayı
            rehberde arayabilirdi ama rehber araması e-postayı yalnız kişi onu görünür
            bıraktıysa eşliyor — destek kişisi adresini gizlediği anda düğme kimseyi bulamaz ve
            kullanıcı nedenini anlayamazdı.

            DESTEK KİŞİSİ TANIMLI DEĞİLSE DÜĞME YOK (8 Ekim 2026, kullanılabilirlik turu). Eskiden
            o durumda yuva «yeni konuşma» kipinde açılıyordu: «Canlı yardım»a basan kişi sıradan
            bir kişi seçicisiyle karşılaşıyordu ve kime yazacağını bilmiyordu — düğmenin verdiği
            söz («canlı yardım») tutulmuyordu. Kullanılamayan eylem devre dışı değil, YOKTUR;
            sohbetin kendisi (köşedeki düğme, «Sohbet» satırı) yerinde, yani kimseye yazma yolu
            kapanmıyor. Destek adresi tanımlanınca (`core.support.contact_email`) düğme geri gelir.
          */}
            {chatAvailable && supportUserId ? (
              <Button
                variant="quiet"
                size="sm"
                className={variant === "sheet" ? "min-h-11 min-w-11" : undefined}
                onClick={() => {
                  /**
                   * KONUŞMA AÇILMIYOR, TASLAK AÇILIYOR. Eskiden düğme `POST /chat/conversations`
                   * çağırıyordu: basıp vazgeçen kişi sohbet listesinde boş bir konuşma bırakıyordu.
                   * Artık yuva destek kişisiyle bir TASLAK açıyor ve satır ancak ilk mesajla
                   * gerçek oluyor. O kişiyle zaten konuşma varsa yuva onu açıyor.
                   */
                  chatDock?.openDock({ draftUserId: supportUserId });
                }}
                aria-label="Canlı yardım — sohbet başlat"
                title="Canlı yardım"
              >
                <Headset size={16} strokeWidth={2} aria-hidden="true" />
              </Button>
            ) : null}
          </div>
        </div>
      }
      {...(variant === "sheet"
        ? { navLabel: "Ana gezinme (çekmece)", onNavigate: () => setMobileNavOpen(false) }
        : {})}
    />
  );

  /**
   * A customer has to be chosen before anything else renders.
   *
   * Only reachable on the shared service, and only when somebody may enter several customers
   * and has not said which. The server refuses to guess in that state — picking "the first"
   * would drop an accountant into whichever client sorted first — so the application must not
   * render a menu, a company selector or a dashboard either. All three would be showing one
   * customer's shape while the session belongs to none.
   *
   * A dedicated installation never reaches this line: its session carries exactly one reachable
   * customer and the server resolves it silently.
   */
  /**
   * PAROLASINI DEĞİŞTİRMESİ GEREKEN HESAP UYGULAMAYI GÖRMEZ (31 Ekim 2026, kullanıcı: «o kullanıcı giriş
   * yapınca default şifresini değiştirmesini isteyecek sistem»). Yöneticinin verdiği parolayla gelen kişi
   * önce kendi parolasını belirliyor; sunucu da o güne kadar başka hiçbir ucu açmıyor (403
   * `PASSWORD_CHANGE_REQUIRED`). Müşteri seçiminden ÖNCE: parola hesabın, müşteriden bağımsız — ve seçicinin
   * kendi ucu da bekleyebilir. Kabuk sorguları bu durumda hiç kurulmuyor (`shellQueriesEnabled`).
   */
  if (mustChangePassword(session)) {
    return (
      <BlockingScreen
        setTitle={setTitleOverride}
        onSignOut={() => signOut.mutate()}
        signingOut={signOut.isPending}
      >
        <PasswordChangeRequired />
      </BlockingScreen>
    );
  }

  if (needsTenantChoice(session)) {
    return (
      <BlockingScreen
        setTitle={setTitleOverride}
        onSignOut={() => signOut.mutate()}
        signingOut={signOut.isPending}
      >
        <TenantPicker session={session} />
      </BlockingScreen>
    );
  }

  /**
   * A customer who may not use the product sees ONE notice, instead of the application.
   *
   * AFTER the customer choice, because on the shared service the licence belongs to the
   * customer and there is no licence to describe until one is chosen — asking the other way
   * round would show "abonelik yok" to an accountant who simply had not picked a client yet.
   *
   * INSTEAD OF, not beside: left inside the shell they would click into screens that each answer
   * 402 separately, learn only that the product is broken, and make the server do the work of
   * refusing every one of those requests.
   */
  if (isSubscriptionBlocked(session)) {
    // `void`, not `reset` directly: `reset` returns a promise and the prop is a void callback.
    // Nothing here awaits the cache clear — the screen is already gone by then.
    return (
      <BlockingScreen setTitle={setTitleOverride}>
        <SubscriptionBlocked session={session} onSignedOut={() => void reset()} />
      </BlockingScreen>
    );
  }

  return (
    <ScreenTitleContext.Provider value={setTitleOverride}>
      <div className="flex h-dvh flex-col bg-bg-app">
        {/**
         * «İÇERİĞE ATLA» — kabuğun İLK odaklanabilir öğesi (8 Ekim 2026, kullanılabilirlik turu;
         * WCAG 2.4.1). Sayfanın kendi içeriğine klavyeyle varmak için üst çubuktan ve raydan ~22
         * Tab geçmek gerekiyordu. Odakta görünür, değilken görünmez (`.skip-link`, app.css).
         *
         * Bağlantı ODAĞI da taşıyor: yalnız `href="#main"` sayfayı kaydırır ama odağı tarayıcıya
         * göre bağlantıda bırakır — sonraki Tab yine üst çubuğa gider. `<main>` `tabIndex={-1}`
         * (MainScroller) ve odağı burada alıyor.
         */}
        <a
          href="#main"
          className="skip-link"
          onClick={(event) => {
            const main = document.getElementById("main");
            if (!main) return;
            event.preventDefault();
            main.focus();
          }}
        >
          İçeriğe atla
        </a>
        {/**
         * ÇEKMECE AÇIKKEN ARKASI `inert` (8 Ekim 2026): kabuğun kendi çizdiği mobil gezinme
         * çekmecesi kip; odak ve ekran okuyucunun imleci arkadaki sayfaya kaçmasın. `contents`,
         * çünkü bu kutu yalnız bir öznitelik taşıyor — çocukları sütunun esnek öğeleri olarak
         * kalıyor, düzen değişmiyor.
         */}
        <div className="contents" {...(mobileNavOpen ? { inert: true } : {})}>
          {/**
           * ABOVE the navbar, not inside it. The customer is the outermost fact about everything
           * below — the menu, the company selector, every number on every screen — and a strip that
           * sits over all of them says so in a way a control tucked into the header cannot.
           *
           * Absent for almost everybody: one reachable customer needs no strip
           * (`showsTenantContext`), which is every person on a dedicated installation and most
           * people on the shared service.
           */}
          {showsTenantContext(session) ? <TenantBar session={session} /> : null}
          {/**
           * Above the navbar with the customer strip, and below it in the stack: which customer you
           * are in comes first, what is about to happen to their licence second.
           */}
          <SubscriptionExpiryBanner session={session} />
          {/**
           * Canlı bağlantı yoksa — ve yalnız birkaç saniyeden uzun sürdüyse — tek satır. Kabukta,
           * çünkü akış ekranın değil uygulamanın: durduğunda sessizce eskiyen şey her ekran.
           */}
          <RealtimeIndicator />
          <AppNavbar
            person={person}
            /**
             * MARKA VE AD KURULUMDAN GELİYOR, koddan değil.
             *
             * İkisi de `system_config_values` içinde birer ayar (`core.brand.*`) ve oturum yanıtı
             * onları `appearance` bloğunda taşıyor. Neden oturumda: o tabloyu okumak
             * `configuration.setting.read` istiyor — yönetici anahtarı — ama marka giriş yapan
             * HERKESE çiziliyor. Ayar yöneticinin, sonucu herkesin.
             *
             * Ayarlanmamışsa ürünün kendi adı ve işareti: kurulum bir şey söylemediğinde ekran boş
             * kalmıyor, ALTEK olduğunu söylüyor.
             */
            /**
             * «ALTEK **ERP**» — ikinci sözcük markanın kendi renginde.
             *
             * Kit `v3.5.0`'a kadar bu yapılamıyordu: `productName` bir dizeydi ve bir dizenin
             * yarısı boyanamaz. Prop `ReactNode` oldu, `productLabel` de düğümün yapamadığı tek
             * şeyi karşılıyor — işaretin harfi adı METİN olarak okuyor ve React bir düğümün
             * metnini çizmeden alamıyor.
             *
             * Rengi `--brand-mark`, çünkü aynı turuncu logoda da duruyor: çubuktaki işaret ile
             * yanındaki sözcük aynı renk. `warning` ailesi değil — o renk «dikkat» demek ve ürünün
             * kendi adı bir uyarı değil.
             */
            productName={brandedName(appearance.productName)}
            productLabel={appearance.productName}
            /**
             * MARKA HER YERDE AYNI GÖRSEL. Üst çubuk şimdiye kadar ürünün baş harfini bir karoya
             * çiziyordu; kit'in yorumu bunun bir yer tutucu OLMADIĞINI söylüyor ve haklı — harf
             * ağ isteği gerektirmiyor, her yoğunlukta keskin kalıyor. Ama bu kurulumun bir markası
             * var ve aynı işaret zaten sekme simgesinde, satıcı hesabının avatarında ve giriş
             * ekranında görünüyor; çubukta harf bırakmak, tek bir yerde farklı görünmek olurdu.
             *
             * `aria-hidden` gerekmiyor: kit markayı zaten gizli çiziyor ve ürün adını yanına
             * yazıyor — yani ekran okuyucu hangi üründe olduğunu adından öğreniyor, görselden değil.
             */
            brandMark={
              <img
                /*
                  PLAKASIZ (25 Ekim 2026). `icon-192.png` sekme simgesinin kendisi ve BEYAZ bir
                  plaka taşıyor — tarayıcı çerçevesinde gerekli, çünkü favicon'un sayfa bağlamı
                  yok ve gri küre koyu bir çerçevede kaybolurdu. Uygulamanın içinde bağlam var:
                  o plaka, koyu kipte üst çubuğa yapıştırılmış bembeyaz bir kare olarak duruyordu.
                  `build-icons.mjs` aynı `favicon.svg`den plakasız bir kopya da üretiyor; işaretin
                  kendisi (turuncu ışınlar, çoğu açık gri olan küre) iki kipte de okunuyor.
                */
                src={appearance.markUrl ?? "/brand/intraline-mark.png"}
                alt=""
                className="size-7 rounded-sm object-contain"
                /**
                 * İŞARET KÜÇÜLMEZ (8 Ekim 2026). Kit, yuvaya gelen görsele `flex: 0 1 auto` veriyor
                 * (uzun bir logo çubuğu taşırmasın diye); kare bir işarette bu, dar çubukta görselin
                 * 9 piksele ezilmesi demekti. Satır içi stil, çünkü kitin kuralı katmansız bir sınıf
                 * kuralı ve yardımcı sınıf (`shrink-0`) ona yetişemiyor; değer px değil, bir anahtar.
                 */
                style={{ flexShrink: 0 }}
              />
            }
            /**
             * Arama sağa yaslı. Kit `v3.3.0` bunu bir prop yaptı; tetiğin masaüstündeki genişliği
             * korunuyor, boşluk öne gidiyor. CSS'i üründen ezmek yerine prop istemenin karşılığı.
             *
             * TELEFONDA DEĞİL (8 Ekim 2026): kitin `end` kuralı medya sorgusu taşımıyor ve 768 pikselin
             * altında da 28rem'lik bir esnek bölge açıyordu — büyüteç çubuğun ortasında kaldı, logo
             * ezildi. Orada kitin kendi telefon kuralı (simgelerin yanına yaslanma) geçerli olsun diye
             * varsayılana dönülüyor. Kit isteği: `end` kuralı `min-width: 768px` içine.
             */
            searchAlign={narrowScreen ? "center" : "end"}
            sidebarOpen={mobileNavOpen}
            sidebarId={MOBILE_NAV_ID}
            onToggleSidebar={() => setMobileNavOpen((open) => !open)}
            onOpenSearch={() => setPaletteOpen(true)}
            notificationCount={notificationUnread}
            /**
             * ZİL ARTIK BİR ŞEY AÇIYOR. Sayaç gösterip basınca hiçbir şey yapmıyordu: bir rozetin
             * tek işi dikkat çekmek ve çektiği dikkatin gidecek yeri yoksa rozet yalan söylüyor.
             * Kit, panel verildiğinde düğmeyi kendiliğinden bir açılır pencereye çeviriyor.
             *
             * Üç panel de gezinmede kapanıyor (`CloseOnNavigation`, 8 Ekim 2026): kit panelleri
             * kendiliğinden kapatmıyor ve açık durumu dışarı vermiyor.
             */
            notificationsPanel={
              <>
                <NotificationsPanel />
                <CloseOnNavigation />
              </>
            }
            approvalCount={approvalBadge}
            approvalsPanel={
              <>
                <ApprovalsPanel />
                <CloseOnNavigation />
              </>
            }
            /**
             * Seçici `useScope`'un kendisini değiştiriyor (`CompanySlot`, 9 Ekim 2026) — ekranlar
             * aynı kaynaktan okuyor. Kit bu yuvayı 1024 pikselin altında gizliyor; orada seçici
             * çekmecenin başında (`rail("sheet")`'in `header`'ı).
             */
            companySlot={<CompanySlot />}
            profilePanel={
              <>
                <ProfileMenu
                  person={person}
                  theme={preference}
                  onThemeChange={setPreference}
                  /*
              `/profile`, `/profil` DEĞİL. Menüdeki «Profilim» hiçbir yere gitmiyordu: rota
              tablosunda `/profile` var, buradaki tek harflik fark onu eşleşmeyen bir adrese
              yolluyordu. Kenar çubuğundaki satır kaldırıldığı için bu artık profile giden TEK
              kapı — kırıklığı da o yüzden görünür oldu.
            */
                  onProfile={() => void navigate("/profile")}
                  {...(settingsHref ? { onSettings: () => void navigate(settingsHref) } : {})}
                  onLogout={() => signOut.mutate()}
                  {...(impersonation
                    ? {
                        extraActions: (
                          <button
                            type="button"
                            className="navbar-menu-row"
                            onClick={() => returnToSelf.mutate()}
                          >
                            <Undo2 size={16} strokeWidth={2} aria-hidden="true" />
                            Kendi hesabıma dön
                          </button>
                        ),
                      }
                    : canImpersonate
                      ? {
                          extraActions: (
                            <button
                              type="button"
                              className="navbar-menu-row"
                              onClick={() => setImpersonateOpen(true)}
                            >
                              <UserRoundCog size={16} strokeWidth={2} aria-hidden="true" />
                              Kullanıcı değiştir
                            </button>
                          ),
                        }
                      : {})}
                />
                <CloseOnNavigation />
              </>
            }
          />

          {/**
           * PERSISTENT BUT QUIET. A support session looks exactly like the real thing, which is the
           * whole point and also the danger — so the notice stays on screen rather than being a
           * toast that disappears before the fourth screen, which is where the change nobody meant
           * to make gets made.
           *
           * IT USED TO SHOUT: a full warning banner with a heading, two sentences and its own
           * padding — four lines of alarm above every page, for a state the operator chose
           * deliberately and already knows they are in. An alarm that is always on is an alarm
           * nobody reads, and it pushed the actual work down the screen every time.
           *
           * One line now, and the ORDER of it is the argument: whose account, then whose session,
           * then the way out. The consequences (audited both sides, critical actions refused) moved
           * to the `title` attribute — available on hover, not occupying the page. They are already
           * stated in full on the dialog that starts the session, which is where somebody is
           * actually deciding.
           */}
          {/*
            AD VE ROL, E-POSTA DEĞİL; İKİ SATIR (31 Ekim 2026, UI/UX denetimi K-A13). Şerit e-postayı
            tek satıra kırpıyordu ve 375 pikselde «… adına» kimin adına çalışıldığını söylemeden
            kesiliyordu; e-posta bir kişiyi değil bir hesabı adlandırıyor. Üst çubuktaki kartla aynı
            ad ve rol (`person`). «Kendi hesabıma dön» telefonda 44 piksel dokunma alanı.

            SATIR SINIRI YOK (31 Ekim 2026, Faz 2 — T-D11). İki satıra kırpmak, uzun bir adda cümleyi
            yine «geliştirme k…» diye kesiyordu: 375 pikselde düğmeden kalan yer 190 piksel kadar.
            Şerit telefonda bir satır uzuyor; kimin adına ve hangi kipte çalışıldığı kesilmiyor.
          */}
          {impersonation ? (
            <div
              className="flex min-h-11 items-center gap-2 border-b border-warning bg-warning-bg px-4 py-1.5 text-xs text-warning-ink md:min-h-0"
              title={
                /* Geliştirme kipi (`supportSessionUnrestricted`, 30 Ekim 2026): reddedilen işlem yok. */
                session.supportSessionUnrestricted
                  ? "Bu oturumdaki her işlem ikinizin de geçmişine yazılır. Geliştirme kipi: kişinin yapabildiği her şey açık."
                  : "Bu oturumdaki her işlem ikinizin de geçmişine yazılır; kritik işlemler reddedilir."
              }
            >
              <UserRoundCog aria-hidden className="size-3.5 shrink-0" />
              <span className="flex min-w-0 flex-col">
                <span className="break-words">
                  <strong className="font-medium">{person.name}</strong> adına
                  {session.supportSessionUnrestricted ? " çalışıyorsunuz · geliştirme kipi" : " görüntülüyorsunuz · destek oturumu"}
                </span>
                {person.roleLabel ? <span className="break-words">{person.roleLabel}</span> : null}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-11 shrink-0 px-2 text-xs md:h-6"
                loading={returnToSelf.isPending}
                onClick={() => returnToSelf.mutate()}
              >
                Kendi hesabıma dön
              </Button>
            </div>
          ) : null}

          <div className="flex min-h-0 flex-1">
            {/* The rail owns its own width and scroller; below 1024px it moves into the sheet. */}
            <div className="hidden lg:flex">{rail("fixed")}</div>

            {/*
          The one <main> in the document (`MainScroller`).

          ALT REZERV SOHBET DÜĞMESİ İÇİN. Yuva `position: fixed` ile sağ altta duruyor ve
          sayfanın alt kenarındaki kontrolün üstüne biniyordu. Rezerv BURADA, `PageShell`'de değil:
          yuva kabuğun bir parçası, sayfanın değil — her sayfada, tek yerde çalışıyor.

          Hem alt boşluk hem `scroll-padding-block-end`, ikisi de kitin `--launcher-reserve`
          jetonundan ve yalnız düğme çiziliyken (`mainScrollerStyle`, gerekçesi orada). Eski
          `pb-24` odaklanan kontrolü korumuyordu ve düğme olmayan sohbet sayfasını telefonda
          kıvrımın altına taşırıyordu. Bağın testi `MainScroller.test.tsx`.
        */}
            <MainScroller>
              <Outlet />
            </MainScroller>
          </div>
        </div>

        <MobileNavScrim open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
        {/*
        MOBİL GEZİNME ÇEKMECESİ BİR KİP İLETİŞİM KUTUSU (8 Ekim 2026): `role="dialog"` +
        `aria-modal`, açılınca odak panelin kendisinde (`tabIndex={-1}`), Tab içeride döner, Escape
        kapatır, kapanınca odak menü düğmesine döner (`useModalSheet`). Arkası `inert` (yukarıda).
      */}
        <div
          ref={mobileNavRef}
          id={MOBILE_NAV_ID}
          role="dialog"
          aria-modal="true"
          aria-label="Gezinme menüsü"
          tabIndex={-1}
          className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] transition-transform duration-200 lg:hidden"
          style={{ transform: mobileNavOpen ? "translateX(0)" : "translateX(-100%)" }}
          /**
           * `inert` GERÇEK BİR BOOLEAN. Burada bir zamanlar `"" as unknown as boolean` yazıyordu —
           * React 18'de `inert`'i tanımayan tiplere karşı bir hileydi. React 19 onu tanıyor ve boş
           * dizeyi FALSE sayıyor: konsola uyarı basıyor ve özniteliği HİÇ yazmıyordu. Yani ekran
           * dışındaki mobil menü klavyeyle hâlâ odaklanılabiliyordu — uyarının kendisi değil,
           * söylediği şey kusurdu. `ChatLauncherHost` aynı özniteliği zaten boolean geçiyor.
           */
          {...(mobileNavOpen ? {} : { inert: true, "aria-hidden": true })}
        >
          {rail("sheet")}
        </div>

        <ImpersonateDialog
          open={impersonateOpen}
          onOpenChange={setImpersonateOpen}
          onStarted={() => {
            reloadIntoIdentity("/my-panel");
            return Promise.resolve();
          }}
        />

        <CommandPalette
          open={paletteOpen}
          onOpenChange={setPaletteOpen}
          groups={paletteGroups}
          onSearch={setPaletteTerm}
          isLoading={recordSearch.isLoading}
          error={recordSearch.error}
          placeholder={searchPlaceholder}
          // Kısayolu kabuk dinliyor (`usePaletteShortcut`): kitinki iki değiştiriciyi her platformda alıyordu.
          shortcut={false}
          onSelect={(item) => {
            const command = shellCommandOf(item.id);
            if (command !== null) {
              // Palet kapanınca çalışır (`pendingCommand`); odak önce açana döner.
              pendingCommand.current =
                command === "impersonate" ? () => setImpersonateOpen(true) : () => returnToSelf.mutate();
              return;
            }
            paletteFocus.markSelected();
            void navigate(item.id.replace(/^(ekran|kayit|eylem):/, ""));
          }}
        />

        {/**
         * The live stream, opened once for the whole shell rather than per screen: the server
         * caps a person at five concurrent streams, so a per-feature connection would evict
         * their other tabs. Renders nothing. Inside the authenticated layout on purpose — the
         * sign-in page must never open one.
         */}
        <RealtimeGate />

        {/**
         * Şoförün çevrimdışı kuyruğu (2 Kasım 2026): bekleyen kayıt, şoför başka ekrandayken de gitsin. Yalnız şoför anahtarıyla
         * ve tembel: başka hesabın paketine girmez. Bir şey çizmez.
         */}
        {permissions.can("operations.self.record") ? (
          <Suspense fallback={null}>
            <FieldQueueGate />
          </Suspense>
        ) : null}

        {/**
         * Sohbetin TEK canlı dinleyicisi. Yuva, sayfa ve konuşma kancası ayrı ayrı abone olduğunda
         * tek bir mesaj üç kez geçersiz kılma yapıyordu — ve liste öneki detay sorgularını da
         * kapsadığı için her seferinde bütün konuşma detayları yeniden okunuyordu (Sohbet 10).
         */}
        <ChatRealtimeBridge meId={session?.user.id ?? null} />

        {/**
         * Arka plan bildirimi bu müşteride de kayıtlı olsun: abonelik TARAYICININ, satır ise
         * (müşteri, kişi) çiftinin. İzin istemez, anahtarı açmaz — yalnız tarayıcı zaten aboneyse
         * satırı tazeler (inceleme, 6. bulgu).
         */}
        <PushSubscriptionSync tenantId={session?.tenant.current?.id ?? null} />

        {/**
         * The chat bubble, bottom-right, on EVERY screen of the product — which is the point:
         * a message must be answerable from the stock screen you happen to be on, without
         * navigating away and losing what you were doing. It shows nothing at all when the chat
         * module is off or the person lacks the key; that check lives inside it.
         */}
        <ChatLauncherHost />
      </div>
    </ScreenTitleContext.Provider>
  );
}
